import { GraduationCap, Briefcase, UserCheck, List } from 'lucide-react';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import React from 'react';
import { InternalProjects } from './internal';
import { ExternalProject } from './exernal/external-projects';
import { Card, CardContent } from '../ui/card';
import UnderlinedTabs from '../tabs';

import { useSearchParams } from 'react-router-dom';


export const ProjectsLayoutComponent = () => {
  const { isStudent, isTeacher, isSchoolAdmin, isCompany } = useClientPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab =
    searchParams.get('type') === 'external'
      ? 'external'
      : 'internal';

  const handleTabChange = (value: string) => {
    setSearchParams(
      { type: value },
      { replace: true }
    );
  };
  
  const [activeInternalSubTab, setActiveInternalSubTab] =
    React.useState('assigned-projects');
  const [activeExternalSubTab, setActiveExternalSubTab] =
    React.useState('external-all');
  const isExternalAdmin = isSchoolAdmin || isCompany;

  React.useEffect(() => {
    setActiveExternalSubTab(isExternalAdmin ? 'external-assigned' : 'external-all');
  }, [isExternalAdmin]);

  React.useEffect(() => {
    const handleSwitchTab = (event: any) => {
      if (!event.detail) return;

      const type = event.detail.toLowerCase();

      setSearchParams(
        { type },
        { replace: true }
      );

      if (type === 'internal') {
        setActiveInternalSubTab('internal-not-assigned');
      } else if (type === 'external') {
        setActiveExternalSubTab(isExternalAdmin ? 'external-assigned' : 'external-all');
      }
    };

    window.addEventListener('switch-project-tab', handleSwitchTab);

    return () => {
      window.removeEventListener('switch-project-tab', handleSwitchTab);
    };
  }, [isExternalAdmin, setSearchParams]);

  const internalSubTabs = [
    {
      value: 'assigned-projects',
      title: 'Assigned Projects',
      icon: <UserCheck size={18} />,
      content: (
        <InternalProjects
          assignedInternalProjects={true}
          isDetailedApplied={true}
          tabName="assigned"
        />
      ),
      subTab: true,
    },
    {
      value: 'internal-not-assigned',
      title: 'Not Assigned',
      icon: <List size={18} />,
      content: (
        <InternalProjects
          assignedInternalProjects={false}
          isDetailedApplied={false}
          tabName="not-assigned"
        />
      ),
      subTab: true,
    },
  ].filter(tab => {
    // If student ONLY (not admin/teacher), restrict to assigned
    if (isStudent && !isTeacher && !isSchoolAdmin) {
      return tab.value === 'assigned-projects';
    }
    return true;
  });

  const externalSubTabs = isExternalAdmin
    ? [
        {
          value: 'external-assigned',
          title: 'Assigned Projects',
          icon: <UserCheck size={18} />,
          content: <ExternalProject assignedExternalProjects />,
        },
        {
          value: 'external-not-assigned',
          title: 'Not Assigned',
          icon: <List size={18} />,
          content: <ExternalProject assignedExternalProjects={false} />,
        },
      ]
    : [
        {
          value: 'external-all',
          title: 'All',
          icon: <List size={18} />,
          content: <ExternalProject />,
        },
      ];

  const mainTabs = [
    {
      value: 'internal',
      title: 'Internal Projects',
      icon: <GraduationCap size={18} />,
      content: (
        <UnderlinedTabs
          tabItems={internalSubTabs}
          makeSticky={false}
          subTab={true}
          makeAbsolute={true}
          value={activeInternalSubTab}
          onValueChange={setActiveInternalSubTab}
        />
      ),
    },
    {
      value: 'external',
      title: 'External Projects',
      icon: <Briefcase size={18} />,
      content: (
        <UnderlinedTabs
          tabItems={externalSubTabs}
          makeSticky={false}
          subTab={true}
          makeAbsolute={true}
          value={activeExternalSubTab}
          onValueChange={setActiveExternalSubTab}
        />
      ),
    },
  ];

  return (
    <Card className='md:bg-transparent border-none bg-card'>
      <CardContent>
        <UnderlinedTabs
          tabItems={mainTabs}
          makeSticky={true}
          value={activeTab}
          onValueChange={handleTabChange}
        />
      </CardContent>
    </Card>
  );
};
